import { CommonModule } from '@angular/common';
import { Component, input } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faBookmark as faBookmarkOutline } from '@fortawesome/free-regular-svg-icons';
import { faBookmark as faBookmarkFilled, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { Observable } from 'rxjs';

import { contentLanguage } from '../../common/content-language';
import { CatalogSku, isAvailableAtSource } from 'src/app/common/CatalogSku';
import { AsyncClickDirective } from 'src/app/directives/async-click.directive';
import { PriceNotePipe } from 'src/app/pipes/price-note.pipe';

/**
 * Catalog item card from the Figma design (page "Page 1", nodes 6:5/6:6/6:7):
 * image on top, then title, seller location and price. Links to the item
 * detail page.
 *
 * Renders the search results grid (`app-search`) and the Merkliste
 * (`app-bookmark-list`). Presentational — no profile lookup and no order
 * action; it carries the #509 availability badge because the grid lists
 * non-orderable listings, but acting on one happens on the detail page. The
 * #562 „Zum Shop" link stays there too: the card's body is one `<a>` to the
 * detail page, and an anchor may not contain another.
 *
 * #652: the bookmark toggle is the one exception, and the reason the root is a
 * positioning container rather than the anchor itself. An interactive control
 * may not be nested in an anchor either, so the button is the anchor's SIBLING,
 * overlaid on the image's top-right corner the way the #509 badge is overlaid
 * on its top-left — keyboard-reachable, and clear of the card's fixed text
 * rhythm (clamped title, bottom-pinned price) that keeps every tile in a row
 * the same height.
 */
@Component({
  imports: [CommonModule, RouterModule, FontAwesomeModule, AsyncClickDirective, PriceNotePipe],
  selector: 'app-catalog-item-card',
  templateUrl: './catalog-item-card.component.html',
  styleUrls: ['./catalog-item-card.component.css'],
})
export class CatalogItemCardComponent {
  readonly catalogSku = input<CatalogSku>();

  /**
   * backend #690: the language this listing's own text is written in, or null when the source
   * declared none. Null renders no `lang` attribute at all — see `contentLanguage`.
   */
  public get contentLanguage(): string | null {
    return contentLanguage(this.catalogSku()?.skuLanguage);
  }


  /**
   * Distance line above the seller name, e.g. "<5km von deinem Standort".
   * The design asks for it but `CatalogSku` has no distance field yet, so the
   * caller passes it; omitted means the line is not rendered.
   */
  readonly distanceLabel = input<string>();

  /**
   * Overrides the small note next to the price. Left empty, the `priceNote`
   * pipe derives the VAT line from `smallBusinessP19UStG`. The design also
   * shows a "Nur Barzahlung" variant, which has no backend field yet.
   */
  readonly priceNote = input<string>();

  /**
   * #652: what the bookmark button runs, as an `[asyncClick]` factory — the card
   * stays presentational and the container owns `BookmarkService`. A factory
   * rather than an `@Output()` because a mutation button in this app is
   * double-click-protected by `[asyncClick]`, which needs the cold Observable
   * (see `AsyncClickDirective`).
   *
   * Null renders no button at all, which is how an anonymous visitor sees the
   * card: the Merkliste API is `[Authorize]` (#652 decision 3a), so an icon
   * would only ever lead to a 401.
   */
  readonly bookmarkAction = input<() => Observable<unknown>>(null);

  /** Whether this listing is on the user's Merkliste — filled vs. outlined icon. */
  readonly isBookmarked = input(false);

  public readonly faMapMarkerAlt = faMapMarkerAlt;

  /** First catalog image, or null so the placeholder background shows through. */
  get imageUrl(): string {
    return this.catalogSku()?.files?.[0]?.imageUrls?.[0]?.mediumImageUrl ?? null;
  }

  /**
   * Backend #562 (F1): „Beim Anbieter erhältlich" — the seller has it, we cannot sell it.
   * Accepts both spellings of the state while the backend rename is rolling out.
   */
  get isAvailableAtSource(): boolean {
    return isAvailableAtSource(this.catalogSku()?.availability);
  }

  /** Solid while bookmarked, outlined while not — the state is visible without colour. */
  get bookmarkIcon() {
    return this.isBookmarked() ? faBookmarkFilled : faBookmarkOutline;
  }

  /**
   * The button carries no text, so the accessible name has to say both what it
   * does and which state it is in.
   */
  get bookmarkLabel(): string {
    return this.isBookmarked()
      ? $localize`:@@catalog.bookmark.remove:Nicht mehr merken`
      : $localize`:@@catalog.bookmark.add:Merken`;
  }
}
